import { zodResolver } from '@hookform/resolvers/zod'
import has from 'lodash/has'
import set from 'lodash/set'
import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form'
import type { z, ZodTypeAny } from 'zod'
import {
  APP_FORM_QUESTION_VARIANTS,
  type AppFormQuestionVariant,
} from '../../constants/featureFlags'
import {
  accountBalanceRefinement,
  bnplRefinement,
  creditRepairRefinement,
  educationRefinement,
  employmentDetailRefinement,
  jobCountAndIncomeRefinement,
  recentAccountsOpenedRefinement,
  recentApplicationsRefinement,
  selfEmploymentDetailRefinement,
  studentDetailRefinement,
  unemploymentDetailRefinement,
} from '../form/schema'
import type { ExtraQuestion } from '../lib/client/submitExtraQuestions'
import EXTRA_QUESTION_LABELS from '../../constants/extraQuestions'

type Refinement = (data: never, ctx: z.RefinementCtx) => void

const getRefinementsForVariant = (variant: AppFormQuestionVariant): Refinement[] => {
  switch (variant) {
    case APP_FORM_QUESTION_VARIANTS.VARIANT_A:
      return [accountBalanceRefinement]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_B:
      return [
        employmentDetailRefinement,
        selfEmploymentDetailRefinement,
        unemploymentDetailRefinement,
        studentDetailRefinement,
      ]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_C:
      return [jobCountAndIncomeRefinement]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_D:
      return [bnplRefinement]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_E:
      return [recentApplicationsRefinement]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_F:
      return [recentAccountsOpenedRefinement]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_G:
      return [creditRepairRefinement]
    case APP_FORM_QUESTION_VARIANTS.VARIANT_H:
      return [educationRefinement]
    default:
      return []
  }
}

// Runs each refinement against `values` and merges any collected issues into
// `baseErrors`. Nested-path issues (e.g. `['names', 'firstName']`) are written
// to the correct nested location via lodash `set`, and any base error already
// present at the same path is preserved. Exported so unit tests can exercise
// path handling directly without invoking the full react-hook-form resolver
// contract.
export const applyRefinements = (
  baseErrors: FieldErrors<FieldValues>,
  values: unknown,
  refinements: Refinement[]
): FieldErrors<FieldValues> => {
  if (refinements.length === 0) return baseErrors

  const collectedIssues: Array<{
    path: (string | number)[]
    code: string
    message: string
  }> = []
  refinements.forEach((refinement) => {
    const refinementCtx = {
      addIssue: (issue: { code: string; message: string; path?: (string | number)[] }) => {
        collectedIssues.push({
          path: issue.path ?? [],
          code: issue.code,
          message: issue.message,
        })
      },
      path: [] as (string | number)[],
    } as unknown as z.RefinementCtx
    refinement(values as never, refinementCtx)
  })

  if (collectedIssues.length === 0) return baseErrors

  const errors = { ...baseErrors } as FieldErrors<FieldValues>
  collectedIssues.forEach((issue) => {
    if (issue.path.length === 0) return
    // Preserve any error already reported by the base zod schema at the same
    // path so refinements never overwrite a more specific base error.
    if (has(errors, issue.path)) return
    set(errors, issue.path, { type: issue.code, message: issue.message })
  })

  return errors
}

// Custom react-hook-form resolver that runs `zodResolver(baseSchema)` first
// and then applies the variant refinements manually against the raw form
// values, merging any new issues into the resolver's error map.
export const createResolverForVariant = <TFieldValues extends FieldValues = FieldValues>(
  baseSchema: ZodTypeAny,
  appFormQuestionVariant: AppFormQuestionVariant
): Resolver<TFieldValues> => {
  const baseResolver = zodResolver(baseSchema) as unknown as Resolver<TFieldValues>
  const refinements = getRefinementsForVariant(appFormQuestionVariant)

  if (refinements.length === 0) return baseResolver

  return async (values, context, options) => {
    const baseResult = await baseResolver(values, context, options)
    const errors = applyRefinements(
      baseResult.errors as FieldErrors<FieldValues>,
      values,
      refinements
    )

    if (errors === baseResult.errors) return baseResult

    return {
      values: {},
      errors: errors as FieldErrors<TFieldValues>,
    }
  }
}

export const buildExtraQuestionsPayload = (formValues: Record<string, unknown>): ExtraQuestion[] =>
  Object.entries(EXTRA_QUESTION_LABELS).reduce<ExtraQuestion[]>((questions, [field, label]) => {
    const value = formValues[field]
    if (value === undefined || value === null || value === '') return questions

    const answer = Array.isArray(value) ? value.join(',') : String(value).replace(/,/g, '')
    if (!answer) return questions

    return [...questions, { question: label, answer }]
  }, [])
